import { useEffect, useState } from 'react';
import { taxonomyService } from '@/services/taxonomy.service';
import { Taxonomy, TaxonomyType } from '@/types/taxonomy';

type TaxonomyOptions = Record<TaxonomyType, { label: string; value: string }[]>;
type AllowInlineCreate = Record<TaxonomyType, boolean>;

const EMPTY_OPTIONS: TaxonomyOptions = {
  [TaxonomyType.ARCHETYPE]: [],
  [TaxonomyType.TPI_SCREEN]: [],
  [TaxonomyType.SB_GOAL]: [],
  [TaxonomyType.EQUIPMENT]: [],
  [TaxonomyType.PATTERN]: [],
  [TaxonomyType.CATEGORY]: [],
  [TaxonomyType.POSITION]: [],
};

// Default every dimension to allowed — multi-selects must not spuriously
// lock while the config call hasn't resolved yet or a type is missing from
// the response.
const DEFAULT_ALLOW_INLINE_CREATE: AllowInlineCreate = {
  [TaxonomyType.ARCHETYPE]: true,
  [TaxonomyType.TPI_SCREEN]: true,
  [TaxonomyType.SB_GOAL]: true,
  [TaxonomyType.EQUIPMENT]: true,
  [TaxonomyType.PATTERN]: true,
  [TaxonomyType.CATEGORY]: true,
  [TaxonomyType.POSITION]: true,
};

const toOptions = (items: Taxonomy[]) =>
  [...items]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.value.localeCompare(b.value))
    .map((item) => ({ label: item.value, value: item.id }));

// Fetch-once lookup hook — guarded with an empty-dependency mount effect so
// it never refetches on every render.
export const useTaxonomy = () => {
  const [options, setOptions] = useState<TaxonomyOptions>(EMPTY_OPTIONS);
  const [allowInlineCreate, setAllowInlineCreate] = useState<AllowInlineCreate>(
    DEFAULT_ALLOW_INLINE_CREATE,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const types = Object.values(TaxonomyType);
        const [results, dimensionConfig] = await Promise.all([
          Promise.all(types.map((type) => taxonomyService.listTaxonomy({ type, isActive: true }))),
          taxonomyService.listDimensionConfig(),
        ]);
        if (cancelled) return;
        const next = { ...EMPTY_OPTIONS };
        types.forEach((type, index) => {
          next[type] = toOptions(results[index]);
        });
        setOptions(next);

        const nextAllowInlineCreate = { ...DEFAULT_ALLOW_INLINE_CREATE };
        dimensionConfig.forEach((config) => {
          nextAllowInlineCreate[config.type] = config.allowInlineCreate;
        });
        setAllowInlineCreate(nextAllowInlineCreate);

        setError(false);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error, allowInlineCreate };
};
